export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? '';

export class UnauthorizedError extends Error {
  constructor(public readonly path: string) {
    super(`Unauthorized: ${path}`);
    this.name = 'UnauthorizedError';
  }
}

// Structured API failure. `message` keeps the legacy "<METHOD> <path> failed: <detail>"
// format that some callers still parse; the extra fields feed the global error toast
// (src/components/ErrorToast) so it can show a friendly message and, on demand, the
// full technical details. Never put request bodies / tokens in here.
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly method: string,
    public readonly path: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly serverMessage: string | null,
    public readonly responseBody: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const MAX_BODY_CHARS = 2000;

// Reads a failed response once; returns the server's {message|error} (if JSON) and
// the raw body text (truncated).
async function readErrorBody(
  res: Response,
): Promise<{ serverMessage: string | null; text: string | null }> {
  let text: string | null = null;
  try {
    text = await res.text();
  } catch {
    return { serverMessage: null, text: null };
  }
  let serverMessage: string | null = null;
  try {
    const data = JSON.parse(text);
    if (data && typeof data.message === 'string') serverMessage = data.message;
    else if (data && typeof data.error === 'string') serverMessage = data.error;
  } catch {
    // not JSON
  }
  const trimmed = text.length > MAX_BODY_CHARS ? `${text.slice(0, MAX_BODY_CHARS)}...` : text;
  return { serverMessage, text: trimmed || null };
}

// fetch() rejects (TypeError) on network failure; wrap so the toast still knows
// the method + path. The message is kept as the original one.
async function fetchOrApiError(
  method: string,
  path: string,
  url: string,
  init: RequestInit,
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new ApiError(msg, method, path, 0, '', null, null);
  }
}

let currentTenantId: number | null = null;

export function setTenantId(tenantId: number | null): void {
  currentTenantId = tenantId;
}

// The active tenant id, for the rare caller that builds its own fetch (e.g. a blob
// download) and must inject X-Tenant-Id the way apiGet/apiPost do.
export function getTenantId(): number | null {
  return currentTenantId;
}

export async function apiGet<T>(
  path: string,
  opts?: { tenantId?: number },
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  // Per-call tenant override (multi-tenant operator screens that target a
  // specific tenant). When absent, fall back to the session's current tenant.
  if (opts?.tenantId !== undefined) {
    headers['X-Tenant-Id'] = String(opts.tenantId);
  } else if (currentTenantId !== null) {
    headers['X-Tenant-Id'] = String(currentTenantId);
  }
  const res = await fetchOrApiError('GET', path, `${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers,
  });
  if (res.status === 401) throw new UnauthorizedError(path);
  if (!res.ok) {
    const { serverMessage, text } = await readErrorBody(res);
    throw new ApiError(
      `GET ${path} failed: ${res.status} ${res.statusText}`,
      'GET',
      path,
      res.status,
      res.statusText,
      serverMessage,
      text,
    );
  }
  return (await res.json()) as T;
}

async function apiBodyRequest<TBody, TResponse>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: TBody,
  opts?: { tenantId?: number },
): Promise<TResponse> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  // Per-call tenant override; fall back to the session's current tenant.
  if (opts?.tenantId !== undefined) {
    headers['X-Tenant-Id'] = String(opts.tenantId);
  } else if (currentTenantId !== null) {
    headers['X-Tenant-Id'] = String(currentTenantId);
  }

  const res = await fetchOrApiError(method, path, `${API_BASE_URL}${path}`, init);

  if (res.status === 401) throw new UnauthorizedError(path);
  if (res.status === 204) return undefined as TResponse;

  if (!res.ok) {
    const { serverMessage, text } = await readErrorBody(res);
    const detail = serverMessage ?? `${res.status} ${res.statusText}`;
    throw new ApiError(
      `${method} ${path} failed: ${detail}`,
      method,
      path,
      res.status,
      res.statusText,
      serverMessage,
      text,
    );
  }

  return (await res.json()) as TResponse;
}

export function apiPost<TBody, TResponse>(
  path: string,
  body?: TBody,
  opts?: { tenantId?: number },
): Promise<TResponse> {
  return apiBodyRequest<TBody, TResponse>('POST', path, body, opts);
}

export function apiPatch<TBody, TResponse>(
  path: string,
  body?: TBody,
): Promise<TResponse> {
  return apiBodyRequest<TBody, TResponse>('PATCH', path, body);
}

export function apiPut<TBody, TResponse>(
  path: string,
  body?: TBody,
): Promise<TResponse> {
  return apiBodyRequest<TBody, TResponse>('PUT', path, body);
}

// DELETE with no request body. Treats 204 (and any 2xx) as success and
// returns void; surfaces a structured server `error`/`message` on failure
// via the shared apiBodyRequest machinery.
export function apiDelete(path: string): Promise<void> {
  return apiBodyRequest<undefined, void>('DELETE', path, undefined);
}

// Multipart POST. Returns the raw Response so callers can read a structured
// error body (e.g. the import run report) on non-2xx. Browser sets the
// multipart boundary, so we must NOT set Content-Type ourselves.
export async function apiPostFormRaw(
  path: string,
  form: FormData,
): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (currentTenantId !== null) headers['X-Tenant-Id'] = String(currentTenantId);
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: form,
  });
  if (res.status === 401) throw new UnauthorizedError(path);
  return res;
}
